import { evaluateClientCapabilities, evaluateClientSubCapability, resolveConfigForClient, } from "./evaluator/evaluateCapabilities";
import {} from "./types/ClientResolvedCapability";
import { normalizeApplicationDefinition, } from "@next-platform/packages-ts-types";
export class ApplicationCapabilitiesStore {
    state = {
        applicationDefinition: null,
        viewer: null,
        featureFlags: null,
        platform: null,
        capabilities: null,
    };
    listeners = new Set();
    capabilityCache = new Map();
    shallowCapabilityCache = new Map();
    subCapabilityCache = new Map();
    serializedApplicationDefinition = "null";
    normalizedDefinitionCache = null;
    evaluationContext = null;
    inputEpoch = 0;
    cacheEpoch = -1;
    isBatching = false;
    hasBatchChanges = false;
    isPendingExternalInvalidation = false;
    getState = () => {
        return this.state;
    };
    subscribe = (listener) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };
    getCapability(key) {
        this.ensureCacheValidity();
        const cached = this.capabilityCache.get(key);
        if (cached !== undefined) {
            return cached;
        }
        if (!this.evaluationContext) {
            return null;
        }
        const result = resolveConfigForClient(key, this.evaluationContext);
        this.capabilityCache.set(key, result);
        return result;
    }
    getCapabilityShallow(key) {
        this.ensureCacheValidity();
        const fullCached = this.capabilityCache.get(key);
        if (fullCached !== undefined) {
            return fullCached;
        }
        const shallowCached = this.shallowCapabilityCache.get(key);
        if (shallowCached !== undefined) {
            return shallowCached;
        }
        if (!this.evaluationContext) {
            return null;
        }
        const result = resolveConfigForClient(key, this.evaluationContext, {
            shallow: true,
        });
        this.shallowCapabilityCache.set(key, result);
        return result;
    }
    getSubCapability(parentKey, subKey) {
        this.ensureCacheValidity();
        const cacheKey = `${parentKey}.${subKey}`;
        const cached = this.subCapabilityCache.get(cacheKey);
        if (cached !== undefined) {
            return cached;
        }
        const fullCached = this.capabilityCache.get(parentKey);
        if (fullCached !== undefined) {
            const subCapabilities = fullCached?.subCapabilities;
            const subResult = subCapabilities?.[subKey] ?? null;
            this.subCapabilityCache.set(cacheKey, subResult);
            return subResult;
        }
        if (!this.evaluationContext) {
            return null;
        }
        const result = evaluateClientSubCapability(parentKey, subKey, this.evaluationContext);
        this.subCapabilityCache.set(cacheKey, result);
        return result;
    }
    getAllCapabilities() {
        if (this.state.capabilities && this.cacheEpoch === this.inputEpoch) {
            return this.state.capabilities;
        }
        this.ensureCacheValidity();
        if (!this.evaluationContext) {
            return null;
        }
        const { applicationDefinition, viewer, featureFlags, platform } = this.evaluationContext;
        const capabilities = evaluateClientCapabilities(applicationDefinition, viewer, featureFlags, platform);
        this.state = { ...this.state, capabilities };
        for (const [capKey, resolved] of Object.entries(capabilities)) {
            this.capabilityCache.set(capKey, resolved);
        }
        return capabilities;
    }
    batch(callback) {
        this.isBatching = true;
        this.hasBatchChanges = false;
        try {
            callback();
        }
        finally {
            this.isBatching = false;
            if (this.hasBatchChanges) {
                this.invalidateCapabilities();
                this.notify();
                this.hasBatchChanges = false;
            }
        }
    }
    setApplicationDefinition(applicationDefinition) {
        if (this.hasEquivalentApplicationDefinition(applicationDefinition)) {
            return;
        }
        this.state = {
            ...this.state,
            applicationDefinition,
        };
        this.serializedApplicationDefinition = this.serializeComparable(applicationDefinition);
        this.commitChange();
    }
    setViewer(viewer) {
        if (this.hasEquivalentViewer(viewer))
            return;
        this.state = {
            ...this.state,
            viewer,
        };
        this.commitChange();
    }
    setFeatureFlags(featureFlags) {
        if (this.state.featureFlags === featureFlags)
            return;
        this.state = {
            ...this.state,
            featureFlags,
        };
        this.commitChange();
    }
    setPlatform(platform) {
        if (this.state.platform === platform)
            return;
        this.state = {
            ...this.state,
            platform,
        };
        this.commitChange();
    }
    invalidateCache() {
        this.invalidateCapabilities();
        if (this.isPendingExternalInvalidation) {
            return;
        }
        this.isPendingExternalInvalidation = true;
        queueMicrotask(() => {
            if (!this.isPendingExternalInvalidation) {
                return;
            }
            this.isPendingExternalInvalidation = false;
            this.notify();
        });
    }
    commitChange() {
        if (this.isBatching) {
            this.hasBatchChanges = true;
            return;
        }
        this.invalidateCapabilities();
        this.isPendingExternalInvalidation = false;
        this.notify();
    }
    invalidateCapabilities() {
        this.inputEpoch++;
        this.state = { ...this.state, capabilities: null };
    }
    ensureCacheValidity() {
        if (this.cacheEpoch === this.inputEpoch) {
            return;
        }
        this.capabilityCache.clear();
        this.shallowCapabilityCache.clear();
        this.subCapabilityCache.clear();
        this.cacheEpoch = this.inputEpoch;
        this.normalizedDefinitionCache = this.state.applicationDefinition ? normalizeApplicationDefinition(this.state.applicationDefinition) : null;
        const { viewer, featureFlags, platform } = this.state;
        if (this.normalizedDefinitionCache && viewer && featureFlags && platform) {
            this.evaluationContext = {
                applicationDefinition: this.normalizedDefinitionCache,
                viewer,
                featureFlags,
                platform,
            };
        }
        else {
            this.evaluationContext = null;
        }
    }
    /**
     * Runtime consumers can provide a newly parsed application definition object
     * whose content is unchanged. We keep the cheap reference check first, then
     * fall back to cached serialization so equivalent payloads do not invalidate
     * capability caches or notify subscribers unnecessarily.
     */
    hasEquivalentApplicationDefinition(applicationDefinition) {
        if (this.state.applicationDefinition === applicationDefinition) {
            return true;
        }
        const serialized = this.serializeComparable(applicationDefinition);
        return serialized !== null && this.serializedApplicationDefinition === serialized;
    }
    /**
     * Viewer is a small, known shape ({@link ViewerContext}). Comparing every
     * field directly avoids the cost and edge cases of generic deep equality
     * while still treating equal roles/permissions arrays as unchanged input.
     *
     * NOTE: If ViewerContext gains new fields, add them here to avoid silently
     * skipping meaningful updates.
     */
    hasEquivalentViewer(viewer) {
        if (this.state.viewer === viewer) {
            return true;
        }
        const previousViewer = this.state.viewer;
        if (previousViewer === null || viewer === null) {
            return false;
        }
        return (previousViewer.applicationId === viewer.applicationId &&
            previousViewer.userId === viewer.userId &&
            previousViewer.subscriptionTier === viewer.subscriptionTier &&
            previousViewer.subscriptionRole === viewer.subscriptionRole &&
            this.areStringArraysEqual(previousViewer.roles, viewer.roles) &&
            this.areStringArraysEqual(previousViewer.permissions, viewer.permissions));
    }
    areStringArraysEqual(previous, next) {
        if (previous === next) {
            return true;
        }
        if (!previous || !next || previous.length !== next.length) {
            return false;
        }
        return previous.every((value, index) => value === next[index]);
    }
    /**
     * Serialization is used only as a defensive fallback for data-only runtime
     * inputs. Returning null keeps the store safe if a future caller passes a
     * non-serializable value.
     */
    serializeComparable(applicationDefinition) {
        try {
            return JSON.stringify(applicationDefinition);
        }
        catch {
            return null;
        }
    }
    reset() {
        this.state = {
            applicationDefinition: null,
            viewer: null,
            featureFlags: null,
            platform: null,
            capabilities: null,
        };
        this.capabilityCache.clear();
        this.shallowCapabilityCache.clear();
        this.subCapabilityCache.clear();
        this.serializedApplicationDefinition = "null";
        this.normalizedDefinitionCache = null;
        this.evaluationContext = null;
        this.inputEpoch = 0;
        this.cacheEpoch = -1;
        this.isBatching = false;
        this.hasBatchChanges = false;
        this.isPendingExternalInvalidation = false;
        this.notify();
    }
    notify() {
        this.listeners.forEach((listener) => listener());
    }
}
export const applicationCapabilitiesStore = new ApplicationCapabilitiesStore();
